import { Line } from '@antv/g2plot'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef } from 'react'

import { Trans } from '@/components'
import { useThemeMode } from '@/contexts/ThemeContext'
import { getChartThemeColors, getLineChartAxisTheme } from '@/utils/chart-theme'
import { getCssVarRgb } from '@/utils/theme-color'

import { HostMonitorItemData, HostMonitorValue } from '../hooks/useNodeMonitorData'

interface HostChartProps {
  value: HostMonitorItemData
  t: (key: string) => string
}

export function HostChart({ value, t }: HostChartProps) {
  const { themeMode } = useThemeMode()
  const id = useMemo(() => Math.random().toString(36).replace('0.', 'ID_'), [])
  const lineRef = useRef<Line>()

  useEffect(() => {
    const { isEmpty } = value
    const values = value.values || []
    const unit = values[0]?.unit
    const usage = unit === '%'

    if (values.length > 0) {
      const chartTheme = getChartThemeColors()
      const axisTheme = getLineChartAxisTheme(themeMode, isEmpty, {
        gridColor: chartTheme.grid,
      })
      const chartFill = `l(270) 0:${getCssVarRgb('--color-highlight', '#365FEF', 0)} 1:${getCssVarRgb(
        '--color-highlight',
        '#365FEF',
        0.5,
      )}`

      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }

      lineRef.current = new Line(id, {
        data: values,
        padding: 'auto',
        xField: 'time',
        yField: 'value',
        xAxis: {
          tickCount: 7,
          label: {
            formatter: (v) => `${dayjs(v).format('HH:mm')}`,
          },
          tickLine: { length: -5 },
          line: axisTheme.axisLine,
          grid: axisTheme.xGrid,
        },
        yAxis: {
          tickCount: usage ? 6 : undefined,
          label: {
            formatter: (v) => `${v}${unit}`,
          },
          min: usage ? 0 : undefined,
          max: usage ? 100 : undefined,
          grid: axisTheme.yGrid,
        },
        area: {
          style: { fill: chartFill },
        },
        tooltip: {
          showMarkers: false,
          customContent(title, data) {
            const item = data[0]
            if (!item) return ''

            const datum = item.data as HostMonitorValue
            const hasAggregation = datum.avg !== undefined
            const formattedTitle = dayjs(title).format('YYYY-MM-DD HH:mm:ss')
            const metricName = t(value.titleKey)

            if (hasAggregation) {
              return `
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
                    <span style="font-size: 12px; font-weight: 600; color: #ffffff;">${formattedTitle}</span>
                    <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">${metricName}</span>
                  </div>
                  <div style="background: rgba(255,255,255,0.08); border-radius: 8px; padding: 4px 8px; display: flex; gap: 16px; align-items: center;">
                    <div style="display: flex; gap: 8px; align-items: center; flex-shrink: 0;">
                      <div style="width: 6px; height: 6px; border-radius: 50%; background: ${chartTheme.highlight}; border: 2px solid ${chartTheme.highlight};"></div>
                      <span style="font-size: 14px; font-weight: 700; color: #ffffff;">${metricName}</span>
                    </div>
                    <div style="display: grid; grid-template-columns: repeat(2, auto); gap: 8px;">
                      <div style="display: flex; gap: 16px;">
                        <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Avg</span>
                        <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${datum.avg}${datum.unit}</span>
                      </div>
                      <div style="display: flex; gap: 16px;">
                        <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Max</span>
                        <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${datum.max}${datum.unit}</span>
                      </div>
                      <div style="display: flex; gap: 16px;">
                        <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Min</span>
                        <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${datum.min}${datum.unit}</span>
                      </div>
                      <div style="display: flex; gap: 16px;">
                        <span style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.65);">Last</span>
                        <span style="font-size: 12px; font-weight: 700; color: #ffffff;">${datum.last}${datum.unit}</span>
                      </div>
                    </div>
                  </div>
                </div>`
            }

            return `
              <div style="display: flex; flex-direction: column; gap: 8px;">
                <div style="display: flex; justify-content: space-between; align-items: center; gap: 16px;">
                  <span style="font-size: 12px; font-weight: 600; color: #ffffff;">${formattedTitle}</span>
                </div>
                <div style="border-radius: 8px; display: flex; gap: 16px; align-items: center;">
                  <div style="font-size: 12px; font-weight: 600; color: #ffffff;">${metricName} ${item.value || 0}${unit}</div>
                </div>
              </div>`
          },
          domStyles: {
            'g2-tooltip': {
              backgroundColor: '#0e121b',
              color: 'white',
              borderRadius: '8px',
              padding: '8px',
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: '500',
              textAlign: 'left',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            },
          },
          fields: ['time', 'value', 'avg', 'max', 'min', 'last'],
          showCrosshairs: true,
          crosshairs: {
            line: {
              style: { lineWidth: 1, lineDash: [5, 3], color: chartTheme.crosshair },
            },
          },
          formatter: (datum) => ({
            ...datum,
            title: dayjs(datum.time).format('YYYY-MM-DD HH:mm:ss'),
            name: t(value.titleKey),
            value: datum.value,
            unit: unit,
          }),
        },
        smooth: true,
        lineStyle: { lineWidth: 1, stroke: chartTheme.highlight },
      })
      lineRef.current.render()
    }

    return () => {
      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }
    }
  }, [JSON.stringify(value), themeMode])

  return (
    <div className="h-[276px] relative">
      <div
        id={id}
        className="h-full w-full overflow-hidden mt-4 text-center text-tertiary t-sm absolute"
      />
      {value?.isEmpty && (
        <div className="absolute h-full w-full flex items-center justify-center">
          <Trans ns="privateRegion" i18nKey="common.noData">
            No Data
          </Trans>
        </div>
      )}
    </div>
  )
}
